import { useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import { api, type BrandStat } from "@/api/client";
import { usePromptsStore } from "@/stores/prompts";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { WinsBarChart } from "./WinsBarChart";

export function WinsPanel() {
  const [overallStats, setOverallStats] = useState<BrandStat[]>([]);
  const [promptStats, setPromptStats] = useState<BrandStat[]>([]);
  const [selectedPromptId, setSelectedPromptId] = useState<string>("");
  const { prompts, fetch: fetchPrompts } = usePromptsStore();

  useEffect(() => {
    api.stats.overall().then(setOverallStats);
    fetchPrompts();
  }, []);

  const handlePromptChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const id = e.target.value;
    setSelectedPromptId(id);
    if (id) {
      const stats = await api.stats.byPrompt(parseInt(id));
      setPromptStats(stats);
    } else {
      setPromptStats([]);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Overall Wins */}
      <Card>
        <CardHeader>
          <CardTitle>Overall Performance</CardTitle>
          <CardDescription>Wins across all comparisons</CardDescription>
        </CardHeader>
        <CardContent>
          {overallStats.length > 0 ? (
            <WinsBarChart stats={overallStats} />
          ) : (
            <EmptyState
              icon={BarChart3}
              title="No data yet"
              description="Run comparisons to see win rates here."
              className="border-0 py-8"
            />
          )}
        </CardContent>
      </Card>

      {/* Filter by Prompt */}
      <Card>
        <CardHeader>
          <CardTitle>By Scoring Template</CardTitle>
          <CardDescription>
            Filter performance by a specific prompt
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-5">
            <Label htmlFor="stats-prompt">Prompt</Label>
            <NativeSelect
              id="stats-prompt"
              value={selectedPromptId}
              onChange={handlePromptChange}
            >
              <option value="">Select a prompt...</option>
              {prompts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </NativeSelect>
          </div>
          {selectedPromptId ? (
            promptStats.length > 0 ? (
              <WinsBarChart stats={promptStats} />
            ) : (
              <p className="text-[13px] text-slate-400 text-center py-6">
                No data for this prompt.
              </p>
            )
          ) : (
            <p className="text-[13px] text-slate-400 text-center py-6">
              Select a prompt to filter results.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
